/**
 * adminHandoff — the copyable "send this to your admin" kit (2026-08-21,
 * firm-wide-approval-as-the-product batch).
 *
 * Since Microsoft's late-2025 secure-by-default change, admin approval is
 * the NORM for mail-reading apps — so the journey hands the user a
 * ready-to-send note that turns the wall into one admin action per firm:
 * Microsoft's tenant-wide approval is one click on an admin-consent link
 * (when the app's client id is known), Google's is the Trusted-app entry in
 * the Admin console (plus the built-in access-request button Google shows
 * blocked users since Aug 2025).
 *
 * Pure module: builders only, no fetches. The note is plain text the USER
 * pastes to their admin — it always carries the one rule that prevents the
 * wrong-identity incident: the admin approves the app, they never sign in
 * with their own account.
 */

export type AdminHandoffProvider = 'microsoft' | 'google';

/** Client-side mirror of the server's email-toolkit → provider mapping
 *  (functions/src/composio/tenantGrants.ts — lockstep-pinned). */
export function providerForEmailToolkitSlug(slug: string): AdminHandoffProvider | null {
  const s = String(slug || '').trim().toLowerCase();
  if (s === 'outlook') return 'microsoft';
  if (s === 'gmail') return 'google';
  return null;
}

/** The one-click tenant-wide approval link from a KNOWN client id (the
 *  ops-configured own-app id, once it exists). Always the `organizations`
 *  endpoint, never `common` — the admin approves THE APP, they do not sign
 *  in as the identity. */
export function buildMicrosoftAdminConsentUrlFromClientId(clientId: string | null | undefined): string | null {
  const id = String(clientId || '').trim();
  if (!/^[0-9a-f-]{8,64}$/i.test(id)) return null;
  return `https://login.microsoftonline.com/organizations/adminconsent?client_id=${encodeURIComponent(id)}`;
}

/** Derive the Google OAuth client id from a minted authorize URL, so the
 *  admin instructions can name the exact id to trust. Only a DIRECT
 *  accounts.google.com authorize URL qualifies — an opaque hop yields null
 *  and the note degrades to name-only instructions. */
export function deriveGoogleOAuthClientId(authorizeUrl: string | null | undefined): string | null {
  if (!authorizeUrl) return null;
  try {
    const parsed = new URL(String(authorizeUrl));
    if (!/(^|\.)accounts\.google\.com$/i.test(parsed.hostname)) return null;
    const clientId = parsed.searchParams.get('client_id');
    if (!clientId || !/^[0-9a-zA-Z._-]{10,120}$/.test(clientId)) return null;
    return clientId;
  } catch {
    return null;
  }
}

export interface AdminHandoffInput {
  provider: AdminHandoffProvider;
  /** The product name the user is asking to use. */
  appDisplayName: string;
  /** Microsoft one-click approval link, when the client id is known. */
  adminConsentUrl?: string | null;
  /** Google client id to trust, when derivable/configured. */
  googleClientId?: string | null;
  /** What the admin will actually SEE on the approval screen when the app
   *  rides a connection partner (the managed-app era: "Composio"). Omit once
   *  prest0n's own apps exist and the screens say prest0n. */
  partnerAppName?: string | null;
  /** The inbox the user is trying to connect — woven in for context. */
  intendedAddress?: string | null;
}

/** The plain-text note the user sends their admin. */
export function buildAdminHandoffNote(input: AdminHandoffInput): string {
  const app = String(input.appDisplayName || 'Prest0n').trim() || 'Prest0n';
  const partner = String(input.partnerAppName || '').trim();
  const screenName = partner ? `"${partner}" (the secure connection service ${app} uses)` : `"${app}"`;
  const forInbox = input.intendedAddress ? ` for my work inbox (${input.intendedAddress})` : ' for my work inbox';
  if (input.provider === 'microsoft') {
    const steps = input.adminConsentUrl
      ? `It's one click: open this link, sign in with your admin account, and approve — this only approves the app for our organization, it does not connect any mailbox.\n${input.adminConsentUrl}`
      : `In the Microsoft Entra admin center (entra.microsoft.com), open Identity > Applications > Enterprise applications. If you see my pending approval request under "Admin consent requests", approve it there; otherwise find ${screenName} and use "Grant admin consent" — this only approves the app for our organization, it does not connect any mailbox.`;
    return [
      `Hi — I'd like to use ${app}${forInbox}, and it needs a one-time approval for our organization.`,
      steps,
      `One important thing: please don't sign in to the email connection itself with your own account — that would connect the wrong identity. Once you've approved the app, I'll sign in as my own inbox myself.`,
      `Thank you!`,
    ].join('\n\n');
  }
  const clientIdLine = input.googleClientId
    ? ` and add this client ID: ${input.googleClientId}`
    : ` and search for ${screenName}`;
  return [
    `Hi — I'd like to use ${app}${forInbox}, and it may need a one-time approval for our organization.`,
    `In the Google Admin console (admin.google.com): Security > Access and data control > API controls > App access control${clientIdLine}, then set it to "Trusted". If I already sent an access request from the blocked screen, approving that request does the same thing.`,
    `This only allows the app for our organization — I'll sign in to my own inbox myself.`,
    `Thank you!`,
  ].join('\n\n');
}
